import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Snackbar from "@mui/material/Snackbar";
import {AxiosError} from "axios";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
    endpoint,
    nomeCognomeRegex, normalizePhoneNumber,
    phoneRegex,
    provincieItaliane,
    required_error,
} from "../../services/shared";
import { ProfessioneEnum } from "../../types";
import { useAuth } from "../../providers/AuthProvider";
import {apiClient} from "../../services/api";

type FormData = z.infer<typeof formSchema>;

export interface DialogQualityCheckProps {
  value: any;
}

export const formSchema = z.object({
  status: z.enum(["ok quality", "respinto da quality"], {
    errorMap: () => ({ message: "Seleziona stato" }),
  }),
  tipologiaRichiesta: z.enum(["Cessione del Quinto", "Mutuo Immobile"], {
    errorMap: () => ({ message: "Seleziona tipologia" }),
  }),
  distributionChannel: z.enum(["hub", "rete"], {
    errorMap: () => ({ message: "Seleziona destinazione" }),
  }),
  nome: z
    .string({ required_error })
    .min(2, "Il nome è obbligatorio")
    .regex(nomeCognomeRegex, "Il nome può contenere solo lettere e spazi"),
  cognome: z
    .string({ required_error })
    .min(2, "Il cognome è obbligatorio")
    .regex(nomeCognomeRegex, "Il cognome può contenere solo lettere e spazi"),
  importo: z.coerce.number().positive("Importo non valido").optional(),
  provincia: z.string({ required_error }).min(1, "Provincia obbligatoria"),
  email: z.string().email("Email non valida").optional(),
  cellulare: z
    .string({ required_error })
    .min(8, "Cellulare non valido")
    .regex(phoneRegex, "Numero di cellulare italiano non valido"),
  nomeAtc: z.string().optional(),
  pIva: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{11}$/.test(val), {
      message: "La Partita IVA deve contenere esattamente 11 cifre",
    }),
  rating: z.string().optional(),
  professione: z.nativeEnum(ProfessioneEnum, {
    errorMap: () => ({ message: "Seleziona una professione" }),
  }),
  sesso: z
    .enum(["F", "M"], {
      errorMap: () => ({ message: "Seleziona un sesso" }),
    })
    .optional(),
  dataNascita: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{2}\/\d{2}\/\d{4}$/.test(val), {
      message: "Formato data non valido",
    }),
  note: z.string({ required_error }).min(10, "Inserisci una nota valida"),
});
const DialogQualityCheck: React.FC<DialogQualityCheckProps> = (props) => {
  const { value } = props;
  const [dialogState, setDialogState] = useState<boolean>(true);
  const [snackState, setSnackState] = useState<boolean>(false);
  const [importoDisplay, setImportoDisplay] = useState("");
  const formatToCurrency = (val: string) => {
    const cleaned = val.replace(/\D/g, "");
    if (!cleaned) return "";
    const number = parseFloat(cleaned) / 100;
    return number.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  useEffect(() => {
    if (value) {
      setDialogState(true);
      reset({nome: value.nome, cognome: value.cognome, cellulare: normalizePhoneNumber(value.cellulare)})
    } else {
      setDialogState(false);
    }
  }, [value]);
  const [snackData, setSnackData] = useState<{
    severity: "success" | "error";
    message: string;
  }>({
    severity: "success",
    message: "Stato lead aggiornato",
  });
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = handleSubmit(async (data) => {
    let res = null;
    try {
      res = await apiClient.post(
        `/v1/leads/quality-check`,
        {
          ...data,
          uid: value.uid,
          status: data.status,
        },
        {
          headers: {
            "Content-Type": `application/json`,
          }
        },
      );
    } catch (e: AxiosError | any) {
      const errorMessage = e instanceof AxiosError ? e?.response?.data.error : String(e);
      console.error("Errore durante il salvataggio", errorMessage);
      setSnackData({
        severity: "error",
        message: "Errore durante il salvataggio",
      });
      setSnackState(true);
    }
    if (res?.data.ok) {
      setSnackData({ severity: "success", message: "Stato lead aggiornato" });
      setSnackState(true);
      reset();
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  });
  const handleClose = () => {
    setDialogState(false);
  };

  return (
    <Dialog onClose={handleClose} open={dialogState} maxWidth="sm" fullWidth>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackData.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackData.message}
        </Alert>
      </Snackbar>
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          fontWeight: "bold",
          fontSize: "1.25rem",
        }}
      >
        Cambio stato
      </DialogTitle>
      <form
        onSubmit={onSubmit}
        style={{
          maxWidth: "720px",
          background: "white",
        }}
        className="flex-col gap-2"
      >
        <DialogContent>
          {/*<h3>Stato corrente del lead</h3>*/}
          {/*<span*/}
          {/*  className={`labelStatus ${value.status === "confermato" ? "confirm" : ""}`}*/}
          {/*>*/}
          {/*  {value.status}*/}
          {/*</span>*/}
          <div className={"flex-col gap-2"}>
            <Controller
                name="status"
                control={control}
                render={({ field }) => (
                    <div style={{ width: "100%", maxWidth: "195px" }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="status-label">Seleziona esito</InputLabel>
                        <Select
                            {...field}
                            style={{ textAlign: "left" }}
                            labelId="status-label"
                            value={field.value || ""}
                            error={!!errors.status}
                        >
                          <MenuItem value="" disabled>
                            Seleziona una opzione
                          </MenuItem>
                          <MenuItem value="ok quality">Approvato</MenuItem>
                          <MenuItem value="ko quality">Respinto</MenuItem>
                        </Select>
                        {errors.status && (
                            <FormHelperText style={{ color: "#d32f2f" }}>
                              {errors.status.message}
                            </FormHelperText>
                        )}
                      </FormControl>
                    </div>
                )}
            />
            <div className="flex-row gap-2">
              <Controller
                  name="tipologiaRichiesta"
                  control={control}
                  render={({ field }) => (
                      <div style={{ width: "100%", maxWidth: "195px" }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="tipologia-label">
                            Tipologia richiesta
                          </InputLabel>
                          <Select
                              {...field}
                              style={{ textAlign: "left" }}
                              labelId="tipologia-label"
                              value={field.value || ""}
                              error={!!(errors as any).tipologiaRichiesta}
                          >
                            <MenuItem value="" disabled>
                              Seleziona una opzione
                            </MenuItem>
                            <MenuItem value="Cessione del Quinto">Cessione</MenuItem>
                            <MenuItem value="Mutuo Immobile">Mutuo</MenuItem>
                          </Select>
                          {(errors as any).tipologiaRichiesta && (
                              <FormHelperText style={{ color: "#d32f2f" }}>
                                {(errors as any).tipologiaRichiesta.message}
                              </FormHelperText>
                          )}
                        </FormControl>
                      </div>
                  )}
              />
              <Controller
                  name="distributionChannel"
                  control={control}
                  render={({ field }) => (
                      <div style={{ width: "100%", maxWidth: "195px" }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="canale-smist">
                            Canale destinazione
                          </InputLabel>
                          <Select
                              {...field}
                              style={{ textAlign: "left" }}
                              labelId="canale-smist"
                              value={field.value || ""}
                              error={!!(errors as any).distributionChannel}
                          >
                            <MenuItem value="" disabled>
                              Seleziona una opzione
                            </MenuItem>
                            <MenuItem value="hub">HUB</MenuItem>
                            <MenuItem value="rete">Rete</MenuItem>
                          </Select>
                          {(errors as any).distributionChannel && (
                              <FormHelperText style={{ color: "#d32f2f" }}>
                                {(errors as any).distributionChannel.message}
                              </FormHelperText>
                          )}
                        </FormControl>
                      </div>
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="nome"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Nome"
                          placeholder="Es. Massimo"
                          value={field.value ?? ""}
                          error={!!errors.nome}
                          helperText={errors.nome?.message}
                      />
                  )}
              />
              <Controller
                  name="cognome"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Cognome"
                          placeholder="Es. Rossi"
                          value={field.value ?? ""}
                          error={!!errors.cognome}
                          helperText={errors.cognome?.message}
                      />
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="importo"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          size="small"
                          label="Importo"
                          placeholder="Es. 10.000,00"
                          value={importoDisplay}
                          onChange={(e) => {
                            const input = e.target.value;
                            const cleaned = input.replace(/\D/g, "");
                            const number = parseFloat(cleaned) / 100;
                            setImportoDisplay(formatToCurrency(input));
                            field.onChange(isNaN(number) ? undefined : number);
                          }}
                          error={!!(errors as any).importo}
                          helperText={(errors as any).importo?.message}
                      />
                  )}
              />
              <Controller
                  name="provincia"
                  control={control}
                  render={({ field }) => (
                      <Autocomplete
                          size="small"
                          disablePortal
                          options={provincieItaliane}
                          sx={{ width: 300 }}
                          value={field.value || null}
                          onChange={(_, newValue) => field.onChange(newValue)}
                          renderInput={(params) => (
                              <TextField
                                  {...params}
                                  label="Provincia"
                                  error={!!(errors as any).provincia}
                                  helperText={(errors as any).provincia?.message}
                              />
                          )}
                      />
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="sesso"
                  control={control}
                  render={({ field }) => (
                      <div style={{ width: "50%", maxWidth: "195px" }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="sesso-label">Seleziona sesso</InputLabel>
                          <Select
                              {...field}
                              style={{ textAlign: "left" }}
                              labelId="sesso-label"
                              value={field.value || ""}
                              error={!!(errors as any).sesso}
                          >
                            <MenuItem value="" disabled>
                              Seleziona una opzione
                            </MenuItem>
                            <MenuItem value="F">Donna</MenuItem>
                            <MenuItem value="M">Uomo</MenuItem>
                          </Select>
                          {(errors as any).sesso && (
                              <FormHelperText style={{ color: "#d32f2f" }}>
                                {(errors as any).sesso.message}
                              </FormHelperText>
                          )}
                        </FormControl>
                      </div>
                  )}
              />
              <Controller
                  name="dataNascita"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          size="small"
                          label="Data di nascita"
                          placeholder="Es. 11/08/1987"
                          value={field.value ?? ""}
                          error={!!(errors as any).dataNascita}
                          helperText={(errors as any).dataNascita?.message}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, ""); // rimuove tutto tranne numeri
                            if (value.length >= 3 && value.length <= 4)
                              value = value.replace(/^(\d{2})(\d{1,2})/, "$1/$2");
                            else if (value.length > 4)
                              value = value.replace(
                                  /^(\d{2})(\d{2})(\d{1,4}).*/,
                                  "$1/$2/$3",
                              );

                            field.onChange(value);
                          }}
                      />
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="cellulare"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          size="small"
                          label="Cellulare"
                          value={field.value || ""}
                          placeholder="Inserisci cellulare"
                          error={!!errors.cellulare}
                          helperText={errors.cellulare?.message}
                      />
                  )}
              />
              <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Email"
                          value={field.value || ""}
                          placeholder="Inserisci email"
                          error={!!(errors as any).email}
                          helperText={(errors as any).email?.message}
                      />
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="nomeAtc"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          size="small"
                          label="Nome ATC"
                          placeholder="Inserisci nome ATC"
                          value={field.value || ""}
                          error={!!(errors as any).nomeAtc}
                          helperText={(errors as any).nomeAtc?.message}
                      />
                  )}
              />
              <Controller
                  name="pIva"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          size="small"
                          label="Partita IVA"
                          placeholder="Inserisci P.IVA"
                          value={field.value || ""}
                          error={!!(errors as any).pIva}
                          helperText={(errors as any).pIva?.message}
                      />
                  )}
              />
            </div>
            <div className="flex-row gap-2">
              <Controller
                  name="rating"
                  control={control}
                  render={({ field }) => (
                      <TextField
                          {...field}
                          size="small"
                          label="Valutazione ATC"
                          placeholder="Inserisci valutazione"
                          value={field.value || ""}
                          error={!!(errors as any).rating}
                          helperText={(errors as any).rating?.message}
                      />
                  )}
              />
              <Controller
                  name="professione"
                  control={control}
                  render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel id="professione-label">
                          Seleziona professione
                        </InputLabel>
                        <Select
                            {...field}
                            style={{ textAlign: "left" }}
                            labelId="professione-label"
                            value={field.value || ""}
                            error={!!(errors as any).professione}
                        >
                          <MenuItem value="" disabled>
                            Seleziona una opzione
                          </MenuItem>
                          {Object.entries(ProfessioneEnum).map(([key, value]) => (
                              <MenuItem key={key} value={value}>
                                {value}
                              </MenuItem>
                          ))}
                        </Select>
                        {(errors as any).professione && (
                            <FormHelperText style={{ color: "#d32f2f" }}>
                              {(errors as any).professione.message}
                            </FormHelperText>
                        )}
                      </FormControl>
                  )}
              />
            </div>
            <Controller
                name="note"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="Note"
                        multiline
                        rows={4}
                        placeholder="Scrivi qualcosa..."
                        fullWidth
                        error={!!(errors as any).note}
                        helperText={(errors as any).note?.message}
                    />
                )}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button variant={"contained"} type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                Elaboro richiesta&nbsp;
                <CircularProgress size={16} color="inherit" />
              </>
            ) : (
              "Conferma"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DialogQualityCheck;
